<template>
  <div
    :class="[
      'form-room-select align-items-start',
      hidden ? 'd-none' : 'd-flex'
    ]"
    :data-test="dataTest"
  >
    <FormInputButton
      :disabled="disabled"
      :label="label"
      :required="required"
      :invalid="invalid && dirty"
      :error="error"
      :wrap="multiselect"
      :data-test="dataTest + 'Btn'"
      class="flex-1"
      @click="disabled || openRoomSelectModal()"
    >
      <slot name="value" v-bind="{ disabled, value, roomValue, visibleValue }">
        <!-- TODO: This breaks when `roomValue` is an array, as RoomTooltip requires :room to be object -->
        <!-- Hence why `v-if` -->
        <div v-if="multiselect">{{ visibleValue }}</div>
        <RoomTooltip
          v-else
          :disabled="!roomValue.displayName"
          :room="roomValue"
        >
          <span>{{ visibleValue }}</span>
        </RoomTooltip>
      </slot>
    </FormInputButton>
    <AppTooltip
      v-if="showRoomCalendar && value && !disabled"
      :inlineTrigger="false"
      :offset="5"
      boundariesElement="window"
      placement="right"
      class="ml-2"
      :style="{ paddingTop: '35px' }"
    >
      <template #default>
        <AppIcon
          :size="20"
          class="text-primary cursor-pointer"
          name="date_range"
          @click.stop="openRoomCalendarModal"
        />
      </template>
      <template #tooltip>
        <span>See Room Calendar</span>
      </template>
    </AppTooltip>
    <RoomSelectModal
      v-if="modals.roomSelect"
      class="form-room-select__modal"
      :value="value"
      :product="view"
      :eventId="eventId"
      :multiselect="multiselect"
      :config="config"
      :meeting="parentValue"
      :minRoomCapacity="minRoomCapacity"
      @input="update"
      @close="closeRoomSelectModal"
    >
      <template #header>
        <slot name="header" v-bind="{ disabled, value, roomValue, visibleValue }">
          <h3 class="font-weight-medium">Select room</h3>
        </slot>
      </template>
      <!-- We need to hide filters.onlyAvailable input in some cases -->
      <template v-if="!mergedSlotsConfig.showFilterOnlyAvailable" #filters:only-available>
        <span></span>
      </template>
      <template #footer>
        <slot name="footer" :update="update">
          <AppButton v-if="!required" variant="secondary" @click="update('')">Select No Room</AppButton>
          <AppButton variant="secondary" class="mr-auto" @click="update('TBA')">Select TBA</AppButton>
        </slot>
      </template>
      <template #close="{ close }">
        <slot name="close" v-bind="{ close }"></slot>
      </template>
    </RoomSelectModal>
    <EventsCalendarModal
      v-if="modals.roomCalendar"
      :roomId="value"
      @close="closeRoomCalendarModal"
    >
      <template #modal-header>
        <AppHeading>Room {{ roomById(value).displayName }} Events Calendar</AppHeading>
      </template>
    </EventsCalendarModal>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import FormMixin from './FormMixin'
import FormInputButton from './FormInputButton'
import RoomTooltip from '@/components/RoomTooltip'
import { inject } from '@vue/composition-api'

function truncateListText (list, limit = 3) {
  if (list.length <= limit) return list.join(', ')
  const visibleElements = list.slice(0, limit).join(', ')
  const hiddenElementsCount = list.length - limit

  return `${visibleElements} and ${hiddenElementsCount} more.`
}

export default {
  name: 'FormRoomSelect',
  mixins: [ FormMixin ],
  components: {
    FormInputButton,
    RoomTooltip,
    RoomSelectModal: () =>
      import(/* webpackChunkName: 'RoomSelectModal' */ '@/components/RoomSelectModal'),
    EventsCalendarModal: () =>
      import(/* webpackChunkName: 'EventsCalendarModal' */ '@em/client/components/EventsCalendarModal'),
  },
  props: {
    eventId: {
      type: String,
      default: '$new',
    },
    view: {
      type: String,
    },
    parentValue: { type: Object },
    multiselect: {
      type: Boolean,
      default: false,
    },
    config: {
      type: Object,
      default: () => ({
        rooms: [],
        excludeRooms: false,
      }),
    },
    slotsConfig: {
      type: Object,
      default: () => ({}),
    },
    minRoomCapacity: {
      type: Number,
      default: null,
    },
  },
  data: () => ({
    modals: {
      roomSelect: false,
      roomCalendar: false,
    },
  }),
  setup () {
    const showRoomCalendar = inject('showRoomCalendar', false)

    return {
      showRoomCalendar,
    }
  },
  computed: {
    ...mapGetters([ 'roomById', 'rooms', 'roomByIdOrEmpty' ]),
    roomValue () {
      return this.multiselect
        ? (this.value || []).map(this.roomByIdOrEmpty)
        : this.roomById(this.value)
    },
    missingRoomsList () {
      if (!this.value || this.value === 'TBA') return []
      if (this.multiselect) return this.value.filter(roomId => !this.roomById(roomId).id)
      else return this.roomValue.id ? [] : [ this.value ]
    },
    visibleValue () {
      return this.multiselect
        ? truncateListText(this.roomValue.map((room, index) => room.displayName || this.value[index]), 4)
        : this.roomValue.displayName || this.value
    },
    mergedSlotsConfig () {
      return {
        showFilterOnlyAvailable: true,
        ...this.slotsConfig,
      }
    },
  },
  created () {
    if (this.missingRoomsList.length) {
      this.fetchRooms({ list: this.missingRoomsList, force: true })
    }
  },
  methods: {
    ...mapActions([ 'fetchRooms' ]),
    openRoomSelectModal () {
      this.modals.roomSelect = true
    },
    closeRoomSelectModal () {
      this.modals.roomSelect = false
      this.$emit('blur')
    },
    openRoomCalendarModal () {
      this.modals.roomCalendar = true
    },
    closeRoomCalendarModal () {
      this.modals.roomCalendar = false
    },
    update (roomId) {
      this.$emit('input', roomId)
      if (!this.multiselect) this.closeRoomSelectModal()
    },
  },
}
</script>

<style scoped>
  .form-room-select__modal {
    height: 100%;
  }
  .form-room-select__modal >>> .modal-content {
    height: 90vh !important;
  }
</style>
